import React, { useState } from 'react'
import { X, Download, ExternalLink, Loader2 } from 'lucide-react'

const CertificatePreviewModal = ({ isOpen, onClose, certificateHTML, certificateLink, mockPrizes }) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = async () => {
    if (!certificateHTML) return
    
    setIsLoading(true)
    try {
      // Create a blob with the HTML content
      const blob = new Blob([certificateHTML], { type: 'text/html' })
      const url = window.URL.createObjectURL(blob)
      
      // Create download link
      const link = document.createElement('a')
      link.href = url
      link.download = 'certificate-preview.html'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading certificate:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenInNewTab = () => {
    if (!certificateHTML) return
    
    const newWindow = window.open('', '_blank')
    newWindow.document.write(certificateHTML)
    newWindow.document.close()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Certificate Preview</h3>
              <p className="text-sm text-gray-600">Preview how the certificate will look to users</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownload}
                disabled={isLoading || !certificateHTML}
                className="btn-secondary flex items-center text-sm"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-1" />
                )}
                Download HTML
              </button>
              <button
                onClick={handleOpenInNewTab}
                disabled={!certificateHTML}
                className="btn-secondary flex items-center text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Open in New Tab
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {certificateHTML ? (
              <div className="space-y-4">
                {/* Preview Frame */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="ml-2 text-xs text-gray-600">Certificate Preview</span>
                    </div>
                  </div>
                  <div className="h-[600px] overflow-auto bg-white">
                    <iframe
                      srcDoc={certificateHTML}
                      className="w-full h-full border-0"
                      title="Certificate Preview"
                    />
                  </div>
                </div>
                
                {/* Prize Information */}
                {mockPrizes && mockPrizes.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-900 mb-3">Virtual Prizes Earned</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {mockPrizes.map((prize, index) => (
                        <div key={index} className={`p-3 rounded-lg border-2 ${
                          prize.rarity === 'legendary' ? 'bg-yellow-100 border-yellow-300' :
                          prize.rarity === 'epic' ? 'bg-purple-100 border-purple-300' :
                          prize.rarity === 'rare' ? 'bg-blue-100 border-blue-300' :
                          'bg-green-100 border-green-300'
                        }`}>
                          <div className="text-center">
                            <div className="text-2xl mb-1">{prize.icon}</div>
                            <div className="font-medium text-sm text-gray-900">{prize.name}</div>
                            <div className="text-xs text-gray-600 mb-1">{prize.description}</div>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              prize.rarity === 'legendary' ? 'bg-yellow-200 text-yellow-800' :
                              prize.rarity === 'epic' ? 'bg-purple-200 text-purple-800' :
                              prize.rarity === 'rare' ? 'bg-blue-200 text-blue-800' :
                              'bg-green-200 text-green-800'
                            }`}>
                              {prize.rarity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certificate Link (if available) */}
                {certificateLink && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Certificate Link</h4>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-xs bg-blue-100 px-2 py-1 rounded break-all">
                        {certificateLink}
                      </code>
                      <button
                        onClick={() => window.open(certificateLink, '_blank')}
                        className="btn-secondary text-xs"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Open
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="w-8 h-8 text-gray-400" />
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No Certificate Preview</h4>
                <p className="text-gray-600">Generate a test certificate to see the preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CertificatePreviewModal
